import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Building2, UserRound, Pencil, Save, Loader2, History } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { errorText } from './SimpleModal';
import { maskAs } from '../lib/autoMask';
import { EditorNameHint } from './EditorNameCheck';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MASK = { editoraCnpj: 'cnpj', editoraCep: 'cep', adminCpf: 'cpf', adminTelefone: 'phone', bibliotecaTelefone: 'phone' };
const DATES = ['editoraDataAbertura', 'adminDataNascimento'];
const GROUPS = [
  ['Editora', Building2, ['bibliotecaNome', 'bibliotecaInstituicao', 'editoraCnpj', 'editoraInscricaoEstadual', 'editoraDataAbertura', 'bibliotecaEndereco', 'editoraCep', 'bibliotecaEmail', 'bibliotecaTelefone', 'editoraSite', 'editoraInstagram']],
  ['Representante', UserRound, ['adminNome', 'adminCpf', 'adminDataNascimento', 'adminEndereco', 'adminTelefone']],
];
const OPTIONAL = ['editoraInscricaoEstadual', 'editoraSite', 'editoraInstagram'];
const show = (k, v) => (MASK[k] ? maskAs(MASK[k], v) : DATES.includes(k) && v ? new Date(`${v}T12:00:00`).toLocaleDateString('pt-BR') : v);

const Field = ({ k, label, value, editing, onChange, original, selfId }) => (
  <div>
    <Label style={{ fontSize: '0.78rem', color: '#718096' }}>{label}{!OPTIONAL.includes(k) && editing ? ' *' : ''}</Label>
    {editing ? (
      <>
        <Input value={value} type={DATES.includes(k) ? 'date' : 'text'} onChange={(e) => onChange(k, e.target.value)} data-testid={`profile-${k}-input`} />
        {k === 'bibliotecaNome' && <EditorNameHint nome={value} excludeId={selfId} initial={original} />}
      </>
    ) : <p style={{ fontSize: '0.92rem', color: '#2D3748', minHeight: 22 }} data-testid={`profile-${k}-value`}>{show(k, value) || '—'}</p>}
  </div>
);

const Logs = ({ logs }) => (logs.length ? (
  <div style={{ marginTop: '1.25rem', borderTop: '1px solid #edf2f7', paddingTop: '1rem' }} data-testid="profile-logs">
    <p style={{ fontWeight: 600, fontSize: '0.85rem', color: '#4a5568', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}><History className="w-4 h-4" />Últimas alterações (enviadas ao administrador)</p>
    {logs.slice(0, 5).map((l) => (
      <div key={l.id} style={{ fontSize: '0.8rem', color: '#4a5568', marginBottom: 6 }}>
        <strong>{new Date(l.at).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })}</strong>: {l.changes.map((c) => `${c.label}: "${c.old || '—'}" → "${c.new || '—'}"`).join('; ')}
      </div>
    ))}
  </div>
) : null);

export const EditorProfileCard = ({ headers, onSaved }) => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = () => axios.get(`${API}/digital/profile`, { headers }).then((r) => { setData(r.data); setForm(r.data.fields); }).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line
  if (!data) return null;
  const change = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const startEdit = () => { setForm(Object.fromEntries(Object.entries(data.fields).map(([k, v]) => [k, MASK[k] ? maskAs(MASK[k], v) : v]))); setEditing(true); };
  const save = async () => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/digital/profile`, form, { headers });
      toast.success(r.data.changes.length ? 'Dados atualizados. O administrador recebeu o registro da alteração.' : 'Nenhuma alteração para salvar.');
      setEditing(false); await load(); onSaved?.();
    } catch (e) { toast.error(errorText(e, 'Não foi possível salvar')); } finally { setBusy(false); }
  };
  return (
    <Card style={{ marginTop: '1.5rem' }} data-testid="editor-profile-card">
      <CardHeader style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <CardTitle style={{ color: '#2D3748' }}>Dados de cadastro</CardTitle>
        {editing ? (
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="outline" onClick={() => setEditing(false)} data-testid="profile-cancel-btn">Cancelar</Button>
            <Button onClick={save} disabled={busy} style={{ background: '#68AAB0' }} data-testid="profile-save-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Salvar</Button>
          </div>
        ) : <Button variant="outline" onClick={startEdit} data-testid="profile-edit-btn"><Pencil className="w-4 h-4 mr-2" />Alterar dados</Button>}
      </CardHeader>
      <CardContent>
        <p style={{ fontSize: '0.8rem', color: '#718096', marginBottom: '1rem' }}>E-mail de acesso: <strong data-testid="profile-login-email">{data.login_email}</strong>. Mudar o nome da editora também muda o link do catálogo (o link antigo continua funcionando).</p>
        {GROUPS.map(([title, Icon, keys]) => (
          <div key={title} style={{ marginBottom: '1.25rem' }}>
            <p style={{ fontWeight: 600, color: '#2D3748', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}><Icon className="w-4 h-4" />{title}</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: '0.9rem' }}>
              {keys.map((k) => <Field key={k} k={k} label={data.labels[k]} value={form[k] || ''} editing={editing} onChange={change} original={data.fields.bibliotecaNome} selfId={data.id} />)}
            </div>
          </div>
        ))}
        <Logs logs={data.logs} />
      </CardContent>
    </Card>
  );
};
